/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../utils/api';
import {
  AnalyticsEvent,
  identifyUser,
  setUserId,
  trackEvent,
} from '../utils/analytics';
import { CombinedRegisterFormData } from '../pages/register/CombinedRegisterForm';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  isEmailVerified: boolean;
  registrationStep: number;
  dateOfBirth?: string;
  address?: {
    street: string;
    street2?: string;
    city: string;
    province: string;
    postalCode: string;
  };
  profileImage?: string;
  store?: {
    id: string;
    name: string;
    image?: string;
    website?: string;
    facebook?: string;
  };
}

interface RegisterData extends CombinedRegisterFormData {
  isAdult: boolean;
  isCanadianResident: boolean;
  excludingQuebec: boolean;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isRefreshing: boolean;
  isAdmin: boolean;
  login: (identifier: string, password: string) => Promise<void>;

  register: (data: RegisterData) => Promise<void>;

  updateRegistration: (step: number, data: any) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;

  checkUsername: (username: string) => Promise<boolean>;
  checkStorename: (name: string) => Promise<boolean>;
  changePassword: (password: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
  resendVerification: () => Promise<void>;
  updateProfileImage: (url: string) => Promise<void>;
  updateStoreImage: (url: string) => Promise<void>;
  updateStore: (store: StoreUpsertData) => Promise<void>;
  fetchStore: () => Promise<void>;
  updateProfile: (
    data:
      | {
          firstName?: string;
          lastName?: string;
          password?: string;
          dateOfBirth?: Date;
          address?: {
            street: string;
            street2?: string;
            city: string;
            province: string;
            postalCode: string;
          };
          profileImage?: File | null;
        }
      | FormData
  ) => Promise<void>;
}

interface StoreUpsertData {
  id?: string;
  name?: string;
  image?: string;
  website?: string;
  facebook?: string;
}

export const getIsAdmin = (token: string | null): any => {
  if (!token) return false;
  try {
    const result = JSON.parse(atob(token.split('.')[1]));
    return !!result.isAdmin || false;
  } catch (e: any) {
    console.error('Error decoding token:', e);
    return false;
  }
};

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isLoading: false,
      isAuthenticated: false,
      isRefreshing: false,
      isAdmin: false,

      login: async (identifier: string, password: string) => {
        set({ isLoading: true });
        try {
          const response = await api.post('/api/auth/login', {
            identifier,
            password,
          });

          const { accessToken, user } = response.data;
          api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

          identifyUser(user);

          set({
            accessToken,
            user,
            isAuthenticated: true,
            isLoading: false,
            isAdmin: getIsAdmin(accessToken),
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true });
        try {
          const response = await api.post('/api/auth/register/combined', data);
          const { accessToken, user } = response.data;
          api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
          set({
            accessToken,
            user,
            isAuthenticated: true,
            isLoading: false,
            isAdmin: getIsAdmin(accessToken),
          });
          trackEvent(AnalyticsEvent.SIGNUP_COMPLETED, {
            userId: user.id,
            hasStore: false,
          });
          setUserId(user.id);
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      updateRegistration: async (step: number, data: any) => {
        set({ isLoading: true });
        try {
          if (step === 4) {
            const response = await api.post(`/api/store`, {
              ...data,
              hasStore: !!data.hasStore,
            });
            const { user } = response.data;
            set((state) => ({
              isLoading: false,
              user:
                state.user && user.store
                  ? {
                      ...state.user,
                      ...user,
                      registrationStep: step,
                      store: {
                        ...(state.user.store ?? {}),
                        ...(user?.store ?? {}),
                      },
                    }
                  : { ...state.user, registrationStep: step },
            }));
          } else {
            const response = await api.patch(
              `/api/auth/register/step${step}`,
              data
            );
            const { user } = response.data;

            set({
              user,
              isLoading: false,
            });
          }
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          const currentUser = useAuth.getState().user;

          if (currentUser) {
            identifyUser(null);
            await api.post('/api/auth/logout');
          }

          try {
            localStorage.removeItem('email_signup_card_dismissed_at');
            localStorage.removeItem('email_signup_popup_dismissed_at');
          } catch {
            // ignore error
          }
          delete api.defaults.headers.common.Authorization;
          set({
            accessToken: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isAdmin: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      forgotPassword: async (email: string) => {
        set({ isLoading: true });
        try {
          await api.post('/api/auth/forgot-password', { email });
          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      resetPassword: async (token: string, password: string) => {
        set({ isLoading: true });
        try {
          await api.post('/api/auth/reset-password', { token, password });
          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      verifyEmail: async (token: string) => {
        set({ isLoading: true });
        try {
          await api.post('/api/auth/verify-email', { token });
          await new Promise((resolve) => setTimeout(resolve, 100));
          set((state) => ({
            user: state.user ? { ...state.user, isEmailVerified: true } : null,
            isLoading: false,
          }));
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      checkUsername: async (username: string) => {
        try {
          const { data } = await api.get(
            `/api/auth/check-username/${username}`
          );
          return data.available;
        } catch (error) {
          console.error(error);
          throw error;
        }
      },

      checkStorename: async (name: string) => {
        try {
          const { data } = await api.get(`/api/store/name/${name}`);
          return data.available;
        } catch (error) {
          console.error(error);
          throw error;
        }
      },

      changePassword: async (newPassword: string) => {
        set({ isLoading: true });
        try {
          await api.post('/api/auth/change-password', {
            password: newPassword,
          });

          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      deleteAccount: async () => {
        set({ isLoading: true });
        try {
          await api.delete('/api/auth/account');
          delete api.defaults.headers.common.Authorization;
          set({
            accessToken: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      resendVerification: async () => {
        set({ isLoading: true });
        try {
          await api.post('/api/auth/resend-verification');
          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      updateProfileImage: async (url: string) => {
        set({ isLoading: true });
        try {
          await api.patch('/api/auth/profile/image', { url });
          set((state) => ({
            isLoading: false,

            user: state.user
              ? { ...state.user, profileImage: url ?? '' }
              : null,
          }));
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      updateStore: async (store: StoreUpsertData) => {
        set({ isLoading: true });
        try {
          const storeResult = await api.put('/api/store', { store });
          const { user } = storeResult.data;
          set((state) => ({
            isLoading: false,
            user:
              state.user && user.store
                ? {
                    ...state.user,
                    store: {
                      ...(state.user.store ?? {}),
                      ...(user?.store ?? {}),
                    },
                  }
                : state.user,
          }));
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      updateStoreImage: async (url: string) => {
        set({ isLoading: true });
        try {
          await api.patch('/api/store/image', { url });
          set((state) => ({
            isLoading: false,
            user:
              state.user && state.user.store
                ? {
                    ...state.user,
                    store: {
                      ...state.user.store,
                      image: url ?? '',
                    },
                  }
                : state.user,
          }));
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      fetchStore: async () => {
        try {
          const response = await api.get('/api/store/my-store');
          const { store } = response.data;
          set((state) => ({
            user: state.user ? { ...state.user, store } : null,
          }));
        } catch (error) {
          console.error('Error fetching store:', error);
          throw error;
        }
      },

      updateProfile: async (
        data:
          | {
              firstName?: string;
              lastName?: string;
              password?: string;
              dateOfBirth?: Date;
              address?: {
                street: string;
                street2?: string;
                city: string;
                province: string;
                postalCode: string;
              };
            }
          | FormData
      ) => {
        try {
          await api.patch('/api/auth/profile', data, {
            headers: {
              'Content-Type': 'application/json',
            },
          });

          // Only update the state with the fields that were sent
          if (!(data instanceof FormData)) {
            set((state) => ({
              ...state,
              user: state.user
                ? {
                    ...state.user,
                    ...Object.fromEntries(
                      Object.entries(data).filter(
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        ([_, value]) => value !== undefined
                      )
                    ),
                  }
                : null,
            }));
          }
          // For FormData, we need to extract the values differently
          else {
            set((state) => {
              if (!state.user) return state;

              const updates: Partial<User> = {};
              for (const [key, value] of data.entries()) {
                if (value !== undefined && value !== 'undefined') {
                  updates[key as keyof User] = value as any;
                }
              }

              return {
                ...state,
                user: {
                  ...state.user,
                  ...updates,
                },
              };
            });
          }
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.accessToken) {
          api.defaults.headers.common.Authorization = `Bearer ${state.accessToken}`;
          state.isAuthenticated = true;
          state.isAdmin = getIsAdmin(state.accessToken);
          if (state.user) {
            identifyUser(state.user);
          }
        }
      },
    }
  )
);
